import React, { useMemo } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { Link, useParams, useNavigate } from "react-router-dom";
import { GlobalStyles } from "../../constants/styles";
import NavBar from "../../components/common/NavBar";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

/* ─────────────────────────────────────────────────────────────
   GQL
────────────────────────────────────────────────────────────── */
const GET_DRAFT_CAMPAIGN = gql`
    query GetDraftCampaign($id: ID!) {
        adCampaign(id: $id) {
            id
            name
            campaignType
            status
            marketplacePromotionIncluded
            startsAt
            endsAt
            countries
            sponsor {
                name
                billingCurrency
            }
            zones {
                key
                name
            }
            targeting {
                countries
                platforms
                sports
            }
            targetOrganizations {
                id
                name
            }
        }
    }
`;

const CREATE_CAMPAIGN_CHECKOUT = gql`
    mutation CreateCampaignCheckout(
        $campaignId: ID!
        $successUrl: String!
        $cancelUrl: String!
    ) {
        createCampaignCheckout(
            campaignId: $campaignId
            successUrl: $successUrl
            cancelUrl: $cancelUrl
        ) {
            success
            errors
            checkoutUrl
        }
    }
`;

/* ─────────────────────────────────────────────────────────────
   Helpers
────────────────────────────────────────────────────────────── */
function safeDate(d) {
    if (!d) return "—";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString();
}

function normalizeList(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string" && value.trim()) return [value.trim()];
    return [];
}

/* ─────────────────────────────────────────────────────────────
   Component
────────────────────────────────────────────────────────────── */
export default function CampaignReview() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    const { data, loading, error, refetch } = useQuery(GET_DRAFT_CAMPAIGN, {
        variables: { id },
        fetchPolicy: "cache-and-network",
    });

    const [createCheckout, { loading: creating }] = useMutation(CREATE_CAMPAIGN_CHECKOUT);

    const campaign = data?.adCampaign;
    const zoneCount = campaign?.zones?.length || 0;
    const sponsorCurrency = String(campaign?.sponsor?.billingCurrency || "USD").toUpperCase();

    const zonesArray = useMemo(
        () => (campaign?.zones?.map((z) => z?.name).filter(Boolean) ?? []),
        [campaign]
    );

    const target = campaign?.targeting || {};
    const countriesFromCampaign = normalizeList(campaign?.countries);
    const countriesFromTarget = normalizeList(target?.countries);
    const countries = countriesFromCampaign.length ? countriesFromCampaign : countriesFromTarget;
    const platforms = normalizeList(target?.platforms);
    const sports = normalizeList(target?.sports);

    const orgNames = useMemo(
        () => (campaign?.targetOrganizations?.map((o) => o?.name).filter(Boolean) ?? []),
        [campaign]
    );
    const isInternalCampaign = campaign?.campaignType === "internal";
    const isOrganizationCampaign = orgNames.length > 0;
    const isMarketplacePromotionIncluded = !!campaign?.marketplacePromotionIncluded;
    const isNoPaymentCampaign = isInternalCampaign || isOrganizationCampaign || isMarketplacePromotionIncluded;

    const onConfirmAndPay = async () => {
        try {
            if (!campaign?.id) {
                toast.error(t('campaignReview.notFound'));
                return;
            }
            if (zoneCount === 0) {
                toast.error(t('campaignReview.addPlacement'));
                return;
            }

            const base = window.location.origin;
            const successUrl = `${base}/advertise/campaign/checkout/success?cid=${encodeURIComponent(campaign.id)}`;
            const cancelUrl = `${base}/advertise/campaign/checkout/cancel?cid=${encodeURIComponent(campaign.id)}`;

            // Currency is resolved by backend from sponsor billing settings.
            const { data: checkoutData } = await createCheckout({
                variables: {
                    campaignId: campaign.id,
                    successUrl,
                    cancelUrl,
                },
            });

            const res = checkoutData?.createCampaignCheckout;
            if (!res?.success) {
                toast.error(res?.errors?.join(", ") || t('campaignReview.couldNotCreateSession'));
                refetch();
                return;
            }

            if (!res.checkoutUrl) {
                toast.success(
                    t(
                        "campaignReview.noPaymentCampaignActivated",
                        {},
                        "Campaign activated. No payment was required."
                    )
                );
                await refetch();
                navigate("/advertise/campaign");
                return;
            }

            window.location.href = res.checkoutUrl;
        } catch (e) {
            toast.error(getLocalizedGraphQLError(e, t, 'campaignReview.checkoutFailed'));
        }
    };

    if (loading && !campaign) {
        return (
            <div className="content-wrapper">
                <div className="content">
                    <div className="p-4">{t('campaigns.loading')}</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="content-wrapper">
                <div className="content">
                    <div className="alert alert-danger m-3">
                        {t('campaignReview.errorLoading')}: {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                    </div>
                </div>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className="content-wrapper">
                <div className="content">
                    <div className="alert alert-warning m-3">{t('campaigns.notFound')}</div>
                </div>
            </div>
        );
    }

    const navBarElem = {
        pageTitle: t('campaignReview.reviewConfirm'),
        links: [
            { pageTitle: t('campaigns.advertising'), navLink: null, active: false },
            { pageTitle: t('campaigns.campaigns'), navLink: "/advertise/campaign", active: false },
            { pageTitle: t('campaignReview.reviewConfirm'), navLink: null, active: true },
        ],
    };

    return (
        <div className="content-wrapper">
            <NavBar navBarElem={navBarElem} />

            <div className="content">
                <div className="row">
                    {/* LEFT: Read-only summary */}
                    <div className="col-12 col-lg-8">
                        <div className="card card-kbola">
                            <div className="card-header">
                                <h3 className="card-title">{t('campaignReview.reviewConfirm')}</h3>
                                <div className="card-tools">
                                    <Link
                                        to={`/advertise/campaign/${campaign?.id}/edit`}
                                        className="btn btn-sm btn-outline-secondary"
                                    >
                                        {t('campaignReview.edit')}
                                    </Link>
                                </div>
                            </div>

                            <div className="card-body">
                                <dl className="row">
                                    <dt className="col-sm-3">{t('campaignReview.campaign')}</dt>
                                    <dd className="col-sm-9">
                                        <div>
                                            <strong>{campaign.name}</strong>
                                        </div>
                                        <small className="text-muted">{t('campaigns.headers.status')}: {campaign.status ? t(`campaigns.status.${campaign.status}`) : "—"}</small>
                                    </dd>

                                    <dt className="col-sm-3">{t('campaigns.headers.sponsor')}</dt>
                                    <dd className="col-sm-9">
                                        {campaign.sponsor?.name || "—"}
                                        <div>
                                            <small className="text-muted">
                                                {t('campaignReview.billingCurrency')}: {sponsorCurrency}
                                            </small>
                                        </div>
                                    </dd>

                                    <dt className="col-sm-3">{t('campaignReview.placements')}</dt>
                                    <dd className="col-sm-9">
                                        {zoneCount ? zonesArray.join(", ") : "—"}
                                    </dd>

                                    <dt className="col-sm-3">{t('campaigns.headers.dates')}</dt>
                                    <dd className="col-sm-9">
                                        {safeDate(campaign.startsAt)} → {safeDate(campaign.endsAt)}
                                    </dd>

                                    <dt className="col-sm-3">{t('campaigns.headers.targeting')}</dt>
                                    <dd className="col-sm-9">
                                        <div>{t('campaignReview.countries')}: {countries.length ? countries.join(", ") : t('campaignReview.all')}</div>
                                        <div>{t('campaignReview.platforms')}: {platforms.length ? platforms.join(", ") : t('campaignReview.all')}</div>
                                        <div>{t('campaignReview.sports')}: {sports.length ? sports.join(", ") : t('campaignReview.all')}</div>
                                        <div>
                                            {t('campaignReview.organizations')}:{" "}
                                            {isInternalCampaign
                                                ? t("campaignReview.allUsers", {}, "All users")
                                                : orgNames.length
                                                    ? orgNames.join(", ")
                                                    : t('campaignReview.all')}
                                        </div>
                                    </dd>
                                </dl>

                                <div
                                    className="alert alert-dark"
                                    style={{ backgroundColor: "#1f1f2e", color: "#fff" }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div style={{ fontSize: "0.9rem" }}>
                                                <strong>{t('campaignReview.total')}</strong>
                                            </div>
                                            <small className="text-muted">
                                                {isInternalCampaign
                                                    ? t(
                                                        "campaignReview.internalNoPaymentRequired",
                                                        {},
                                                        "Internal AngeonX Sports campaign"
                                                    )
                                                : isOrganizationCampaign
                                                    ? t(
                                                        "campaignReview.enterpriseNoPaymentRequired",
                                                        {},
                                                        "Included with Enterprise organization targeting"
                                                    )
                                                    : isMarketplacePromotionIncluded
                                                        ? t(
                                                            "campaignReview.enterpriseMarketplaceNoPaymentRequired",
                                                            {},
                                                            "Included with your Enterprise league subscription"
                                                        )
                                                    : t('campaignReview.calculatedOnStripe', { currency: sponsorCurrency })}
                                            </small>
                                        </div>
                                        <div
                                            style={{
                                                fontSize: "1.2rem",
                                                fontWeight: 700,
                                                color: GlobalStyles.colors.canary,
                                            }}
                                        >
                                            {isNoPaymentCampaign
                                                ? t("campaignReview.noPaymentRequired", {}, "No payment required")
                                                : t('campaignReview.calculatedAtCheckout')}
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between">
                                    <Link
                                        to={`/advertise/campaign/${campaign.id}/edit`}
                                        className="btn btn-secondary"
                                    >
                                        {t('campaignReview.backToEdit')}
                                    </Link>
                                    <button
                                        className="btn btn-kbola-canary"
                                        onClick={onConfirmAndPay}
                                        disabled={creating}
                                    >
                                        {creating ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm mr-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                {t('campaignReview.redirecting')}
                                            </>
                                        ) : (
                                            <>
                                                {isNoPaymentCampaign
                                                    ? t("campaignReview.activateCampaign", {}, "Activate Campaign")
                                                    : t('campaignReview.confirmPay')} <i className="fas fa-arrow-right ml-2" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Simple tips */}
                    <div className="col-12 col-lg-4">
                        <div className="card card-outline card-kbola">
                            <div className="card-header bg-kbola">
                                <h3 className="card-title">{t('campaignReview.beforeYouPay')}</h3>
                            </div>
                            <div className="card-body">
                                <ul className="mb-0">
                                    <li>{t('campaignReview.tip1')}</li>
                                    <li>{t('campaignReview.tip2')}</li>
                                    <li>{t('campaignReview.tip3')}</li>
                                    <li>{t('campaignReview.tip4')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sticky footer */}
                <div
                    className="border-top"
                    style={{
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999,
                        background: GlobalStyles.colors.woodsmoke,
                        borderTop: "1px solid rgba(var(--kbs-primary-rgb, 0,173,255), 0.4)",
                    }}
                >
                    <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                        <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                            {t('campaignReview.footerText')}
                        </div>
                        <div>
                            <button
                                className="btn btn-secondary mr-2"
                                onClick={() => navigate(-1)}
                                disabled={creating}
                            >
                                {t('campaigns.cancelLabel')}
                            </button>
                            <button
                                className="btn btn-kbola-canary"
                                onClick={onConfirmAndPay}
                                disabled={creating}
                            >
                                {creating
                                    ? t('campaignReview.redirecting')
                                    : isNoPaymentCampaign
                                        ? t("campaignReview.activateCampaign", {}, "Activate Campaign")
                                        : t('campaignReview.confirmPay')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
